import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box, CircularProgress, Card, CardHeader, CardContent } from "@material-ui/core";

import { AppBar, Tabs, Tab } from "@mui/material";

import HazardComponent from "./HazardComponent";
import useGetHazardsValues from "./customHooks/useGetHazardsValues";
import { userHasSomePermissions } from "./../util/permissionsUtils";
import { PageTabs } from "./hazardIdEnum";

type HazardAssessmentProps = {
  hazardsList: Hazard[];
  latitude: number;
  longitude: number;
};

const RiskAssessmentTitle = () => {
  const { t } = useTranslation();
  return (
    <Typography
      style={{
        fontSize: "1.3rem",
        fontWeight: 400,
      }}
    >
      {t("menu.riskEvaluation")}
    </Typography>
  );
};

const HeaderRow = () => {
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" wrap="nowrap">
      <Grid item xs={4}>
        <Typography variant="h6" align="center">
          {t("riskAssessment.showOnMap")}
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography variant="h6" align="center">
          {t("riskAssessment.value")}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="h6" align="center">
          {t("riskAssessment.colors")}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="h6" align="center">
          {t("riskAssessment.level")}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="h6" align="center">
          {t("riskAssessment.rating")}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const HazardsAssessment: React.FC<HazardAssessmentProps> = ({ hazardsList, latitude, longitude }) => {
  const { hazardValues, loading } = useGetHazardsValues({
    hazardsList,
    lat: latitude,
    lng: longitude,
  });

  return (
    <Box pt={1}>
      <Card>
        {loading ? (
          <Grid container justifyContent="center">
            <Box p={2}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : (
          <CardContent style={{ paddingTop: "0" }}>
            {hazardValues.length > 0 && !loading && (
              <>
                <HeaderRow />
                {hazardValues.map((hazardWithValue, index) => (
                  <HazardComponent hazardProp={hazardWithValue} key={index}></HazardComponent>
                ))}
              </>
            )}
          </CardContent>
        )}
      </Card>
    </Box>
  );
};

export const HazardsAssessmentNoTabs: React.FC<HazardAssessmentProps> = ({
  hazardsList,
  latitude,
  longitude,
}) => {
  return (
    <Box mt={2}>
      <Card>
        <CardHeader title={<RiskAssessmentTitle />} />
        <HazardsAssessment hazardsList={hazardsList} latitude={latitude} longitude={longitude} />
      </Card>
    </Box>
  );
};

export const HazardsAssessmentWithTabs: React.FC<HazardAssessmentProps> = ({
  hazardsList,
  latitude,
  longitude,
}) => {
  const { t } = useTranslation();

  const [requestedTabIdx, setRequestedTabIdx] = useState(PageTabs.preferredTab);

  const handleTabChange = (event: any, newValue: PageTabs) => {
    setRequestedTabIdx(newValue);
  };

  const preferredHazardsList = hazardsList.filter((hazard) => hazard.pageTab === PageTabs.preferredTab);
  const otherHazardsList = hazardsList.filter(
    (hazard) => !hazard.pageTab || hazard.pageTab === PageTabs.otherTab,
  );
  const warningsHazardsList = hazardsList.filter((hazard) => hazard.pageTab === PageTabs.warningTab);
  const cocoOldHazardsList = hazardsList.filter((hazard) => hazard.pageTab === PageTabs.cocoOldTab);
  const cocoNewHazardsList = hazardsList.filter((hazard) => hazard.pageTab === PageTabs.cocoNewTab);

  const socioEnvironmentList = hazardsList.filter(
    (hazard) => hazard.pageTab === PageTabs.socioEnvironmentTab,
  );

  return (
    <Box mt={2}>
      <Card>
        <CardHeader title={<RiskAssessmentTitle />} />
        <AppBar position="static" color="default" elevation={0}>
          <Tabs
            value={requestedTabIdx}
            textColor="primary"
            indicatorColor="primary"
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ overflowX: "auto" }} // Ensures horizontal scroll for tabs
          >
            <Tab value={PageTabs.preferredTab} sx={{ minWidth: "fit-content" }} label={t("menu.preferred")} />
            <Tab value={PageTabs.otherTab} sx={{ minWidth: "fit-content" }} label={t("menu.other")} />
            {warningsHazardsList.length > 0 && (
              <Tab value={PageTabs.warningTab} sx={{ minWidth: "fit-content" }} label={t("menu.warnings")} />
            )}
            {/* Currently we assume that all users have identical permissions for coco old and coco new */}
            {/* // TODO: update to warnings approach */}
            {cocoNewHazardsList.length > 0 && (
              <Tab value={PageTabs.cocoNewTab} sx={{ minWidth: "fit-content" }} label={t("menu.coco")} />
            )}
            {cocoOldHazardsList.length > 0 && (
              <Tab
                value={PageTabs.cocoOldTab}
                sx={{ minWidth: "fit-content" }}
                label={`${t("menu.coco")} old`}
              />
            )}
            {socioEnvironmentList.length > 0 && (
              <Tab
                value={PageTabs.socioEnvironmentTab}
                sx={{ minWidth: "fit-content" }}
                label={t("menu.socioEnvironmental")}
              />
            )}
          </Tabs>
        </AppBar>

        <Box
          style={{
            maxHeight: "650px",
            overflowY: "auto", // Enable vertical scrollbar
            overflowX: "auto", // Enable horizontal scrollbar
          }}
        >
          {requestedTabIdx === PageTabs.preferredTab && (
            <HazardsAssessment hazardsList={preferredHazardsList} latitude={latitude} longitude={longitude} />
          )}

          {requestedTabIdx === PageTabs.otherTab && (
            <HazardsAssessment hazardsList={otherHazardsList} latitude={latitude} longitude={longitude} />
          )}

          {warningsHazardsList.length > 0 && requestedTabIdx === PageTabs.warningTab && (
            <HazardsAssessment hazardsList={warningsHazardsList} latitude={latitude} longitude={longitude} />
          )}

          {cocoOldHazardsList.length > 0 && requestedTabIdx === PageTabs.cocoOldTab && (
            <HazardsAssessment hazardsList={cocoOldHazardsList} latitude={latitude} longitude={longitude} />
          )}

          {cocoNewHazardsList.length > 0 && requestedTabIdx === PageTabs.cocoNewTab && (
            <HazardsAssessment hazardsList={cocoNewHazardsList} latitude={latitude} longitude={longitude} />
          )}

          {socioEnvironmentList.length > 0 && requestedTabIdx === PageTabs.socioEnvironmentTab && (
            <HazardsAssessment hazardsList={socioEnvironmentList} latitude={latitude} longitude={longitude} />
          )}
        </Box>
      </Card>
    </Box>
  );
};
